import { Trans, useLingui } from "@lingui/react/macro";

import { useRankLookup } from "@/features/matches";

import type { DashboardPlayer } from "../../model/TeamDashboard";
import { DashboardCard } from "./DashboardCard";
import { dayDate } from "./dayDate";

const DAY_MS = 24 * 60 * 60 * 1000;

type NewPlayersProps = {
  players: DashboardPlayer[];
  total: number;
  // Сегодня по UTC (`YYYY-MM-DD`): «сегодня» и «вчера» у последнего визита.
  today: string;
};

// «Новые игроки»: последние пришедшие за период — когда пришли и были, сколько разобрали
// и решили, ранг и откуда пришли.
export const NewPlayers = ({ players, total, today }: NewPlayersProps) => {
  const { i18n, t } = useLingui();
  const rank = useRankLookup();
  const joined = new Intl.DateTimeFormat(i18n.locale, {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
  const day = new Intl.DateTimeFormat(i18n.locale, {
    day: "numeric",
    month: "short",
    // eslint-disable-next-line lingui/no-unlocalized-strings -- часовой пояс, а не текст
    timeZone: "UTC",
  });
  const lastSeen = (value: string | null | undefined): string => {
    if (value == null) {
      return "—";
    }
    const daysAgo = Math.round((dayDate(today).getTime() - dayDate(value).getTime()) / DAY_MS);
    if (daysAgo === 0) {
      return t`today`;
    }
    if (daysAgo === 1) {
      return t`yesterday`;
    }
    return day.format(dayDate(value));
  };
  const origin = (player: DashboardPlayer): string => {
    const name = player.origin_name ?? "";
    switch (player.origin) {
      case "source":
        return name;
      case "invite":
        return t`invited by ${name}`;
      case "other":
        return name === "" ? t`other site` : name;
      case "direct":
        return t`direct visit`;
      case "unknown":
        return "—";
    }
  };
  const shown = players.length;

  return (
    <DashboardCard
      title={<Trans>New players</Trans>}
      aside={
        <span className="text-[13px] text-subtle">
          <Trans>
            Latest {shown} of {total}
          </Trans>
        </span>
      }
    >
      {players.length === 0 ? (
        <p className="text-[13px] text-muted">
          <Trans>No new players in this period.</Trans>
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[760px] border-collapse text-sm">
            <thead>
              <tr className="text-left text-xs text-subtle">
                <th scope="col" className="pt-1.5 pr-2.5 pb-2 font-semibold">
                  <Trans>Player</Trans>
                </th>
                <th scope="col" className="px-2.5 pt-1.5 pb-2 font-semibold">
                  <Trans>Joined</Trans>
                </th>
                <th scope="col" className="px-2.5 pt-1.5 pb-2 font-semibold">
                  <Trans>Last seen</Trans>
                </th>
                <th scope="col" className="px-2.5 pt-1.5 pb-2 text-right font-semibold">
                  <Trans>Analyses</Trans>
                </th>
                <th scope="col" className="px-2.5 pt-1.5 pb-2 text-right font-semibold">
                  <Trans>Tasks</Trans>
                </th>
                <th scope="col" className="px-2.5 pt-1.5 pb-2 font-semibold">
                  <Trans>Rank</Trans>
                </th>
                <th scope="col" className="pt-1.5 pb-2 pl-2.5 font-semibold">
                  <Trans>From</Trans>
                </th>
              </tr>
            </thead>
            <tbody>
              {players.map((player) => (
                <tr
                  key={player.user_id}
                  data-player-id={player.user_id}
                  className="border-t border-border/60"
                >
                  <td className="py-2 pr-2.5">
                    <span className="flex items-center gap-2.5">
                      {player.avatar_url === "" ? (
                        <span
                          aria-hidden="true"
                          className="inline-flex size-8 shrink-0 items-center justify-center rounded-full bg-border text-[11px] font-semibold"
                        >
                          {player.display_name.slice(0, 1).toUpperCase()}
                        </span>
                      ) : (
                        <img
                          src={player.avatar_url}
                          alt=""
                          className="size-8 shrink-0 rounded-full bg-surface-raised object-cover"
                        />
                      )}
                      <span className="font-semibold">{player.display_name}</span>
                    </span>
                  </td>
                  <td className="px-2.5 py-2 text-foreground-soft">
                    {joined.format(new Date(player.joined_at))}
                  </td>
                  <td className="px-2.5 py-2 text-foreground-soft">
                    {lastSeen(player.last_active_day)}
                  </td>
                  <td className="px-2.5 py-2 text-right font-mono tabular-nums">
                    {player.analyses}
                  </td>
                  <td className="px-2.5 py-2 text-right font-mono tabular-nums">
                    {player.drill_answers}
                  </td>
                  <td className="px-2.5 py-2 text-foreground-soft">
                    {player.rank_badge == null ? "—" : rank(player.rank_badge).name}
                  </td>
                  <td className="py-2 pl-2.5 text-foreground-soft">{origin(player)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </DashboardCard>
  );
};
